import React, { useContext, useEffect, useState } from 'react';
import { InputField } from '@trp-beacon-ui-kit/react-controls';
import LoginCTA from '../../components/LoginCTA/LoginCTA';
import { FlowContext } from '../../core/flow-engine';
import { withDefault } from '../../core/util';
import styles from './style.css';
import {
  getHeaderText,
  getURLTickers,
  redirectRolloverNoAuth,
  renderPrimaryEmailTitleWithTooltip,
  renderDobTitleWithTooltip,
  renderAmountTitleWithTooltip
} from './util';
import PhoneNumber from '../../components/PhoneNumber/PhoneNumber';
import Heading from '../../components/Typography/Heading/Heading';
import ProfileScreenButtons from '../../components/Button/ProfileScreenButtons';
import {
  firstnameValidation,
  lastnameValidation,
  restrictedFirstNameValidation,
  restrictedLastNameValidation
} from '../../flows/shared/validations';
import { accountTypes, handleInputFormat } from '../../flows/shared/utils';
import { generateGeneralTestID } from '../../generateAutomationTestIDs';
import ExistingAgreementsBlockModal from '../../components/ExistingAgreementModals/ExistingAgreementsBlockModal';

/* eslint-disable complexity */
const ProfileScreen = () => {
  const { formProps, appState, back } = useContext(FlowContext);
  const {
    store: {
      rpsDirectRollover,
      authenticated,
      enabled,
      prospect,
      app,
      amount,
      accountType,
      existingAgreements
    }
  } = appState;
  const { customer, fromIRAAccountType, iraAccountType } = app;

  useEffect(() => {
    redirectRolloverNoAuth(rpsDirectRollover, authenticated);
  }, [rpsDirectRollover, authenticated]);

  const isRollover = accountType === accountTypes.ROLLOVER;

  // these will exist if user is authenticated or a RJ user
  const firstName = customer?.name?.firstName;
  const lastName = customer?.name?.lastName;
  const primaryEmailAddress = customer?.primaryEmailAddress;
  const dob = customer?.dob;

  const [dobValue, setDobValue] = useState(withDefault(dob, ''));
  const [amountValue, setAmountValue] = useState(withDefault(amount, ''));

  const disableFlag = authenticated && enabled;

  // multi-fund buy query check
  // capture tickers to pass to InvestmentReviewScreen
  const multiBuyTickers =
    !rpsDirectRollover && getURLTickers(window.location.search);

  // US661619 (COMPLIANCE) - block clients opening agreements that match an existing agreement
  const hasExistingAgree = existingAgreements && existingAgreements?.length > 0;

  const firstNameValidation = disableFlag
    ? firstnameValidation
    : restrictedFirstNameValidation;
  const lastNameValidation = disableFlag
    ? lastnameValidation
    : restrictedLastNameValidation;

  return (
    <div className="profile-fields">
      <Heading
        text={getHeaderText(
          authenticated,
          firstName,
          accountType,
          fromIRAAccountType,
          iraAccountType
        )}
      />

      <LoginCTA authenticated={authenticated} />

      <div className="name-fields">
        <div data-e2e="firstname" className="name-column">
          <InputField
            type="text"
            title="First Name"
            {...formProps.firstName}
            disabled={disableFlag}
            defaultValue={firstName}
            maxLength="35"
            validation={firstNameValidation}
            className="blocked_data"
            data-name={generateGeneralTestID(
              'BasicPI',
              'AccountOwnerFirstName'
            )}
          />
        </div>
        <div data-e2e="lastname" className="name-column">
          <InputField
            type="text"
            title="Last Name"
            {...formProps.lastName}
            disabled={disableFlag}
            defaultValue={lastName}
            maxLength="35"
            validation={lastNameValidation}
            className="blocked_data"
            data-name={generateGeneralTestID('BasicPI', 'AccountOwnerLastName')}
          />
        </div>
      </div>
      <div data-e2e="email">
        <InputField
          type="email"
          title={renderPrimaryEmailTitleWithTooltip()}
          {...formProps.primaryEmailAddress}
          defaultValue={primaryEmailAddress}
          disabled={
            prospect
          } /* disable email fields for prospect due to email verification */
          className="blocked_data"
          data-name={generateGeneralTestID('BasicPI', 'AccountOwnerEmail')}
        />
      </div>
      <div data-e2e="dateofbirth">
        <InputField
          title={renderDobTitleWithTooltip()}
          aria-describedby="hint"
          type="text"
          onChange={evt => handleInputFormat(evt, 'DATE', setDobValue)}
          placeholder="mm/dd/yyyy"
          maxLength="10"
          {...formProps.dob}
          value={dobValue}
          disabled={enabled}
          className="blocked_data"
          data-name={generateGeneralTestID('BasicPI', 'AccountOwnerBirthDate')}
        />
        <dd id="hint" style={{ display: 'none' }}>
          This is a required field. Please enter a two digit month, a two digit
          day and a four digit year for the Date of Birth value.
        </dd>
      </div>
      <div data-e2e="amount">
        <InputField
          onChange={evt =>
            handleInputFormat(evt, 'DECIMAL_CURRENCY', setAmountValue)
          }
          maxLength="18"
          disabled={rpsDirectRollover}
          title={renderAmountTitleWithTooltip()}
          value={amountValue}
          {...formProps.amount}
          className="blocked_data"
          data-name={generateGeneralTestID('BasicPI', 'InvestmentAmount')}
        />
      </div>

      <ProfileScreenButtons
        multiBuyTickers={multiBuyTickers}
        handleBackClick={back('landing')}
      />

      <PhoneNumber screen="default" />
      <style jsx>{styles}</style>

      {/* block account opening if existing agreement flag is true */}
      {isRollover && hasExistingAgree && <ExistingAgreementsBlockModal />}
    </div>
  );
};

export default ProfileScreen;
